import { z } from 'zod';

/**
 * Line timetables: departures from a line's first station plus the travel time to each station
 * give every station's times. Stored per station as before (`schedules`, read by the app); the plan
 * that produced them is kept so the line can be edited again as a whole.
 */

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const time = z.string().regex(HHMM, 'Use HH:MM');

/** Minutes since midnight. */
export const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
/** HH:MM for minutes since midnight (past midnight wraps to the next day's 00:00). */
export const fromMinutes = (m: number) => {
  const v = ((Math.round(m) % 1440) + 1440) % 1440;
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};
/** Unique, sorted. */
export const sortTimes = (times: string[]) => [...new Set(times)].sort();

/** "From 06:00 to 22:00 every 15 minutes": 06:00, 06:15 … 22:00. */
export const departurePattern = z
  .object({ from: time, to: time, every: z.coerce.number().int().min(1).max(240) })
  .refine((p) => toMinutes(p.to) >= toMinutes(p.from), { message: 'Ends before it starts', path: ['to'] });
export type DeparturePattern = z.infer<typeof departurePattern>;

export function expandPattern(p: DeparturePattern): string[] {
  const out: string[] = [];
  for (let m = toMinutes(p.from); m <= toMinutes(p.to); m += p.every) out.push(fromMinutes(m));
  return out;
}

/** One kind of day: the rules used to fill it in, and the resulting departures (edited by hand too). */
export const dayPlan = z.object({
  patterns: z.array(departurePattern).max(20).default([]),
  departures: z.array(time).max(400).default([]),
});
export type DayPlan = z.infer<typeof dayPlan>;

export const timetablePlanInput = z.object({
  week: dayPlan,
  weekend: dayPlan,
  /** Minutes from the first station, one per station of the line (in line order). */
  offsets: z
    .array(
      z.object({ station: z.string().regex(/^[a-f\d]{24}$/i), minutes: z.coerce.number().min(0).max(600) }),
    )
    .max(200),
});
export type TimetablePlanInput = z.infer<typeof timetablePlanInput>;

/** Times at a station: every departure from the first station plus the travel time to it. */
export function stationTimes(departures: string[], minutes: number): string[] {
  return sortTimes(departures.map((d) => fromMinutes(toMinutes(d) + minutes)));
}

/**
 * Travel time from the first station to a station, read from existing timetables: for each time
 * at the station, the latest first-station departure up to `maxMinutes` before it; the median of
 * those gaps. Null when nothing matches.
 */
export function inferOffset(first: string[], times: string[], maxMinutes = 180): number | null {
  const starts = first.map(toMinutes).sort((a, b) => a - b);
  if (!starts.length || !times.length) return null;
  const gaps: number[] = [];
  for (const t of times.map(toMinutes)) {
    let best: number | null = null;
    for (const s of starts) if (s <= t && t - s <= maxMinutes) best = t - s;
    if (best !== null) gaps.push(best);
  }
  if (!gaps.length) return null;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)]!;
}

/** What saving would change at a station: times added and removed. */
export function diffTimes(before: string[], after: string[]) {
  const a = new Set(before);
  const b = new Set(after);
  return {
    added: after.filter((t) => !a.has(t)),
    removed: before.filter((t) => !b.has(t)),
  };
}
